import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader, Surface, Empty } from "../components/Primitives";
import { Activity, Mail, MessageSquare, RefreshCcw, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const StatusBadge = ({ ok, mode }) => (
  <span
    data-testid={ok ? "status-ok" : "status-mock"}
    className={
      "inline-flex items-center gap-1.5 text-xs font-mono px-2 py-0.5 rounded " +
      (ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200")
    }
  >
    <span className={"inline-block w-1.5 h-1.5 rounded-full " + (ok ? "bg-emerald-500" : "bg-amber-500")} />
    {mode}
  </span>
);

export default function Diagnostics() {
  const { user } = useAuth();
  const isSupport = !!user?.is_support;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testPhone, setTestPhone] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/diagnostics");
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isSupport) load(); }, [isSupport]);

  const sendTestEmail = async () => {
    if (!testEmail) return;
    try {
      const { data: r } = await api.post("/admin/diagnostics/test-email", { to: testEmail });
      toast.success(`Email envoyé à ${testEmail} (status: ${r.result?.status})`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const sendTestSms = async () => {
    if (!testPhone) return;
    try {
      const { data: r } = await api.post("/admin/diagnostics/test-sms", { phone: testPhone });
      toast.success(`SMS envoyé à ${testPhone} (status: ${r.result?.status})`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!isSupport) {
    return (
      <div className="space-y-6">
        <PageHeader title="Diagnostics" subtitle="Outil support" />
        <Empty>Cette page est réservée au support technique.</Empty>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Diagnostics intégrations"
        subtitle="État Resend (email) · Africa's Talking (SMS) · MOCK vs PRODUCTION"
        actions={
          <button data-testid="reload-diagnostics" className="btn-ghost flex items-center gap-2" onClick={load} disabled={loading}>
            <RefreshCcw size={14} />{loading ? "Chargement…" : "Rafraîchir"}
          </button>
        }
      />

      {!data && <Surface><div className="p-8 text-center text-[var(--text-muted)]">Chargement…</div></Surface>}

      {data && (
        <>
          {/* Statut global */}
          <div className="grid md:grid-cols-2 gap-4">
            <Surface title="Email (Resend)" testId="diag-resend">
              <div className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <Mail size={18} className="text-[var(--text-muted)]" />
                  <StatusBadge ok={data.resend.configured} mode={data.resend.mode} />
                </div>
                <dl className="text-sm space-y-1.5">
                  <div className="flex justify-between"><dt className="text-[var(--text-muted)]">API key</dt><dd className="font-data">{data.resend.api_key_present ? data.resend.api_key_prefix : "❌ ABSENTE"}</dd></div>
                  <div className="flex justify-between"><dt className="text-[var(--text-muted)]">Sender</dt><dd className="font-data text-xs">{data.resend.sender_email}</dd></div>
                  <div className="flex justify-between"><dt className="text-[var(--text-muted)]">Reply-to</dt><dd className="font-data text-xs">{data.resend.reply_to}</dd></div>
                  <div className="flex justify-between"><dt className="text-[var(--text-muted)]">SDK chargé</dt><dd>{data.resend.sdk_loaded ? "✅" : "❌"}</dd></div>
                </dl>
                {!data.resend.configured && (
                  <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
                    ⚠️ Resend n'est pas configuré. Les emails ne sont pas envoyés (mode MOCK).
                  </div>
                )}
                <div className="pt-2 border-t flex gap-2">
                  <input
                    data-testid="test-email-input"
                    type="email"
                    placeholder="destinataire@exemple.com"
                    className="input-base text-xs"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                  />
                  <button data-testid="send-test-email" className="btn-primary text-xs flex items-center gap-1.5" onClick={sendTestEmail} disabled={!testEmail}>
                    <Send size={12} />Test
                  </button>
                </div>
              </div>
            </Surface>

            <Surface title="SMS (Africa's Talking)" testId="diag-at">
              <div className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <MessageSquare size={18} className="text-[var(--text-muted)]" />
                  <StatusBadge ok={data.africastalking.configured} mode={data.africastalking.mode} />
                </div>
                <dl className="text-sm space-y-1.5">
                  <div className="flex justify-between"><dt className="text-[var(--text-muted)]">Username</dt><dd className="font-data">{data.africastalking.username_present ? data.africastalking.username_prefix : "❌ ABSENT"}</dd></div>
                  <div className="flex justify-between"><dt className="text-[var(--text-muted)]">API key</dt><dd>{data.africastalking.api_key_present ? "✅ présente" : "❌ absente"}</dd></div>
                  <div className="flex justify-between"><dt className="text-[var(--text-muted)]">Sender short code</dt><dd className="font-data text-xs">{data.africastalking.sender_short_code}</dd></div>
                  <div className="flex justify-between"><dt className="text-[var(--text-muted)]">SMS_DEV_MODE</dt><dd className="font-data">{data.africastalking.sms_dev_mode_blocks_send ? "❌ TRUE (bloque)" : "✅ false"}</dd></div>
                  <div className="flex justify-between"><dt className="text-[var(--text-muted)]">SDK chargé</dt><dd>{data.africastalking.sdk_loaded ? "✅" : "❌"}</dd></div>
                </dl>
                {!data.africastalking.configured && (
                  <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
                    ⚠️ AT n'est pas en mode production. Les SMS ne partent pas.
                  </div>
                )}
                <div className="pt-2 border-t flex gap-2">
                  <input
                    data-testid="test-sms-input"
                    type="tel"
                    placeholder="+221 77 …"
                    className="input-base text-xs"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                  />
                  <button data-testid="send-test-sms" className="btn-primary text-xs flex items-center gap-1.5" onClick={sendTestSms} disabled={!testPhone}>
                    <Send size={12} />Test
                  </button>
                </div>
              </div>
            </Surface>
          </div>

          {/* Historique emails */}
          <Surface title="10 derniers emails envoyés" testId="diag-emails-history">
            {data.recent_emails.length === 0 ? <Empty>Aucun email envoyé.</Empty> : (
              <table className="data-table">
                <thead><tr><th>Date</th><th>Destinataire</th><th>Sujet</th><th>Statut</th></tr></thead>
                <tbody>
                  {data.recent_emails.map((e, i) => (
                    <tr key={i}>
                      <td className="font-data text-xs">{e.created_at?.slice(0,16).replace("T"," ")}</td>
                      <td className="font-data text-xs">{e.to}</td>
                      <td className="text-xs">{e.subject}</td>
                      <td>
                        <span className={
                          "text-xs font-mono px-1.5 py-0.5 rounded " +
                          (e.status === "sent" ? "bg-emerald-50 text-emerald-700" :
                           e.status === "mock" ? "bg-amber-50 text-amber-700" :
                           "bg-rose-50 text-rose-700")
                        }>{e.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Surface>

          {/* Historique SMS */}
          <Surface title="10 derniers SMS envoyés" testId="diag-sms-history">
            {data.recent_sms.length === 0 ? <Empty>Aucun SMS envoyé.</Empty> : (
              <table className="data-table">
                <thead><tr><th>Date</th><th>Numéro</th><th>Type</th><th>Statut</th></tr></thead>
                <tbody>
                  {data.recent_sms.map((s, i) => (
                    <tr key={i}>
                      <td className="font-data text-xs">{s.created_at?.slice(0,16).replace("T"," ")}</td>
                      <td className="font-data text-xs">{s.phone}</td>
                      <td className="text-xs">{s.purpose}</td>
                      <td>
                        <span className={
                          "text-xs font-mono px-1.5 py-0.5 rounded " +
                          (s.status === "sent" ? "bg-emerald-50 text-emerald-700" :
                           s.status === "mock" ? "bg-amber-50 text-amber-700" :
                           "bg-rose-50 text-rose-700")
                        }>{s.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Surface>

          <div className="text-xs text-[var(--text-muted)] flex items-center gap-2">
            <Activity size={12}/>
            Environnement : MongoDB {data.environment.mongo_url_set ? "OK" : "❌ NON CONFIGURÉ"}
            · SMS_DEV_MODE = {String(data.environment.sms_dev_mode)}
          </div>
        </>
      )}
    </div>
  );
}
